import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Phone, Mail, CheckCircle, Shield, 
  Search, Wrench, TrendingUp, Building2
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import SEO from '@/components/shared/SEO';

// Program services data
const programServices = [
  {
    icon: Shield,
    title: 'Routine Maintenance Services',
    items: [
      'Scheduled deep cleaning',
      'Surface treatments and protective coatings',
      'Stain removal and restorative cleaning'
    ]
  },
  {
    icon: Search,
    title: 'Inspection & Preventative Care',
    items: [
      'Regular condition assessments',
      'Identification of high-wear areas',
      'Recommendations for preventative improvements'
    ]
  },
  {
    icon: Wrench,
    title: 'Repair & Restoration',
    items: [
      'Tile replacement and repair',
      'Surface restoration',
      'Corrective maintenance for damaged or worn areas'
    ]
  },
  {
    icon: TrendingUp,
    title: 'Performance Optimization',
    items: [
      'Maintenance strategies tailored to specific flooring types',
      'Guidance on cleaning procedures and best practices',
      'Recommendations to extend the lifecycle of flooring systems'
    ]
  }
];

// Facility types
const facilityTypes = [
  'Class A corporate offices',
  'Healthcare facilities',
  'Retail environments',
  'Hospitality spaces',
  'Education campuses',
  'Industrial and high-traffic facilities'
];

// Benefits
const benefits = [
  'Extend the lifespan of flooring assets',
  'Maintain a clean and professional environment',
  'Improve safety by addressing wear and slip risks',
  'Reduce unexpected repair and replacement costs',
  'Simplify facility management through scheduled service'
];

const MaintenanceProgramPage = () => {
  return (
    <Layout>
      <SEO 
        title="Flooring Maintenance Programs"
        description="Protect your flooring investment with Portico's professional maintenance programs. Routine cleaning, inspections, repairs, and performance optimization for commercial floors."
        canonicalUrl="/maintenance"
      />
      {/* Hero Section */}
      <section className="bg-primary-bg text-primary-foreground py-16 md:py-20">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="maintenance-hero-title">
              Flooring Maintenance Program
            </h1>
            <p className="text-xl text-primary-foreground/90">
              Protect your flooring investment and keep your facility looking its best with the Portico Systems Flooring Maintenance Program.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <p className="text-lg text-muted-foreground mb-6">
              Commercial flooring experiences constant wear from foot traffic, environmental conditions, and daily operations. Without proper care, even high-quality flooring systems can deteriorate prematurely, leading to unnecessary replacement costs and disruptions to your business.
            </p>
            <p className="text-lg text-muted-foreground">
              Our maintenance program is designed to extend the life, performance, and appearance of your flooring, helping you maintain a clean, safe, and professional environment year-round.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Proactive Care Section */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Shield className="w-12 h-12 text-primary mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Proactive Care for Long-Term Performance
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Rather than reacting to flooring issues after they occur, our program focuses on preventative maintenance and scheduled service. By addressing wear early and maintaining flooring systems properly, organizations can significantly reduce long-term repair and replacement costs.
                </p>
                <p>
                  Portico Systems works with your team to develop a custom maintenance plan based on your facility type, traffic levels, and flooring materials.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/services/proactive-care.jpg"
                alt="Flooring Maintenance Services"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Program Services Section */}
      <section className="section-padding bg-background" data-testid="maintenance-services-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Program Services May Include
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our maintenance program can include a range of services designed to preserve the condition and performance of your flooring.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-portico p-6 md:p-8"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-xl mb-4">{service.title}</h3>
                <ul className="space-y-2">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Designed for Commercial Facilities */}
      <section className="section-padding bg-primary-light text-white">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Building2 className="w-12 h-12 mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Designed for Commercial Facilities
              </h2>
              <p className="text-white/90 mb-6">
                Our maintenance programs support a wide range of environments, including:
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {facilityTypes.map((facility) => (
                  <li key={facility} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                    <span>{facility}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/services/commercial-facilities.jpg"
                alt="Commercial Facilities"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* A Smarter Way Section */}
      <section className="section-padding bg-secondary text-secondary-foreground">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              A Smarter Way to Manage Flooring
            </h2>
            <p className="text-secondary-foreground/90 text-lg mb-8">
              A structured maintenance program helps organizations:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Get Started CTA */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8 md:p-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                  Get Started
                </h2>
                <p className="text-muted-foreground mb-6">
                  Portico Systems can help evaluate your current flooring conditions and develop a maintenance program tailored to your facility.
                </p>
                
                {/* Contact Info */}
                <div className="space-y-4 mb-8">
                  <a
                    href="tel:+18772858899"
                    className="flex items-center gap-3 text-foreground hover:text-primary transition-colors"
                  >
                    <Phone className="w-5 h-5 text-primary" />
                    <span>+1 877 285 8899</span>
                  </a>
                  <a
                    href="mailto:sales@porticosystems.com"
                    className="flex items-center gap-3 text-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="w-5 h-5 text-primary" />
                    <span>sales@porticosystems.com</span>
                  </a>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Link to="/contact">
                  <Button size="lg" data-testid="maintenance-contact-btn">
                    Contact Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/resources#maintenance">
                  <Button variant="outline" size="lg">
                    View Resources
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default MaintenanceProgramPage;
